import { Accordion, Button, Card, Col, Container, Row, Stack } from "react-bootstrap";
import { useLocale } from "@/hooks/useLocale";
import { DynamicText } from "@/ui/DynamicText";
import classes from "./styles.module.scss";
import clsx from "classnames";
import { generateOptionsArray } from "@/functions/generateOptionsArray";
import { useEffect, useState } from "react";
import { useLink } from "@/hooks/useLink";
import Address from "@/assets/Address.svg";
import Logo from "@/assets/LogoDarkEN.svg";
import Email from "@/assets/Email.svg";
import LinkedIn from "@/assets/LinkedIn.svg";
import { team_helper } from "@/helpers/team_helper";
import Star from "@/assets/Star.svg";

export const TeamPageFeedSection = ({ className }) => {
  const locale = useLocale();
  const link = useLink();
  const [team] = useState(team_helper.fetch());
  const [ready, setReady] = useState(false);
  const [specialities] = useState(generateOptionsArray(team, `speciality_${locale}`));
  const [locations] = useState(generateOptionsArray(team, `location`));
  const [speciality, setSpeciality] = useState("");
  const [location, setLocation] = useState("");
  const [computedItems, setComputedItems] = useState([]);

  useEffect(() => {
    setComputedItems(
      team
        .filter((i) =>
          speciality.trim() !== ""
            ? `speciality_${locale}` in i
              ? i[`speciality_${locale}`].toLowerCase().trim() === speciality.toLowerCase().trim()
              : false
            : true
        )
        .filter((i) =>
          location.trim() !== "" ? i.location.toLowerCase().trim() === location.toLowerCase().trim() : true
        )
    );
  }, [speciality, location, team, locale, setComputedItems]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setReady(true);
    }, 1500);

    return () => {
      clearTimeout(timeout);
    };
  }, []);

  return (
    <div className={className}>
      <div className={clsx(classes.filters, { loading: !ready })}>
        <Container>
          <Stack className={clsx(classes.filters__form, "p-3")} gap={{ xs: 2, md: 4 }}>
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <DynamicText path={`page.team.filter.speciality.placeholder.${locale}`} />
                </Accordion.Header>
                <Accordion.Body>
                  <Stack className="flex-row flex-wrap" gap={2}>
                    <Button size="sm" variant="secondary" onClick={() => setSpeciality("")}>
                      Show All
                    </Button>
                    {specialities.map((i) => (
                      <Button
                        key={i}
                        size="sm"
                        variant="primary"
                        onClick={() => setSpeciality(i)}
                        dangerouslySetInnerHTML={{ __html: i }}
                      />
                    ))}
                  </Stack>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  <DynamicText path={`page.team.filter.location.placeholder.${locale}`} />
                </Accordion.Header>
                <Accordion.Body>
                  <Stack className="flex-row flex-wrap" gap={2}>
                    <Button size="sm" variant="secondary" onClick={() => setLocation("")}>
                      Show All
                    </Button>
                    {locations.map((i) => (
                      <Button
                        key={i}
                        size="sm"
                        variant="primary"
                        onClick={() => setLocation(i)}
                        dangerouslySetInnerHTML={{ __html: i }}
                      />
                    ))}
                  </Stack>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </Stack>
        </Container>
      </div>
      <Container className="py-5">
        <Row>
          {computedItems.map((i, k) => (
            <Col key={k} xs={12} sm={6} md={4} lg={3} className="mb-4">
              <Card as="a" className={clsx(classes.card)} href={link(`team.member`, i.url_slug)}>
                <Stack className={classes.card__links}>
                  {i.email && (
                    <a className={classes.card__email} href={`mailto:${i.email}`}>
                      <Email />
                    </a>
                  )}
                  {i.linkedin && (
                    <a className={classes.card__linkedin} href={i.linkedin} target="_blank">
                      <LinkedIn />
                    </a>
                  )}
                </Stack>
                {i.photo ? (
                  <Card.Img src={i.photo} />
                ) : (
                  <div className="ratio ratio-1x1 bg-white">
                    <div className="d-flex align-items-center p-5">
                      <Logo className="w-100" />
                    </div>
                  </div>
                )}
                <Card.Body className="bg-light">
                  <Card.Title>{i.name}</Card.Title>
                  <Card.Text as="div" className="mb-3" style={{ minHeight: "45px" }}>
                    <p className="mb-2">{i[`position_${locale}`]}</p>
                    {i[`speciality_${locale}`] && (
                      <Stack className={clsx(classes.card__specialty, "flex-row gap-2 small")}>
                        <Star width={10} /> {i[`speciality_${locale}`]}
                      </Stack>
                    )}
                  </Card.Text>
                  <dl className={clsx({ "opacity-0": !i.location })}>
                    <dt>
                      <Address />
                    </dt>
                    <dd>{i.location}</dd>
                  </dl>
                  <Button variant="outline-primary" className="float-end pb-0">
                    <DynamicText path={`profile.card.link.${locale}`} />
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </Container>
    </div>
  );
};
